import * as React from 'react'
import { observer } from 'mobx-react'
import Workspace from 'iam/models/Workspace'
import {
  ViewHeadingProps,
  ViewHeading,
  ViewHeadingSize,
} from 'components/ViewHeading'
import { Id } from 'types/definitions'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownDivider,
  DropdownScroll,
} from 'components/Dropdown'
import Icon from 'components/Icon'
import styles from './WorkspaceSelectorViewHeading.sass'

export interface IWorkspaceSearcher {
  setSearchText: (val: string) => void
  searchText: string
  editing: boolean
  startEditing: () => void
  stopEditing: () => void
}

interface WorkspaceSelectorViewHeadingProps
  extends Omit<ViewHeadingProps, 'isDropdown' | 'secondaryText'> {
  workspaces: Workspace[]
  value: Workspace
  onChange: (workspace: Workspace) => unknown
  dropdownTitle?: string
  allOptionDropdownItem?: React.ReactNode
  allOptionText: string | null
  alwaysUseDropdown?: boolean
  searcher?: IWorkspaceSearcher
  renderName?: (workspace: Workspace) => React.ReactNode
  viewHeadingSize?: ViewHeadingSize
  showClearButton?: boolean
}

export default observer(function WorkspaceSelectorViewHeading({
  workspaces,
  value,
  onChange,
  allOptionDropdownItem,
  searcher,
  renderName,
  allOptionText,
  dropdownTitle = 'Workspaces',
  alwaysUseDropdown = false,
  viewHeadingSize = 'normal',
  showClearButton = false,
  ...rest
}: WorkspaceSelectorViewHeadingProps) {
  const shouldUseDropdown = alwaysUseDropdown || workspaces.length > 1
  const heading =
    allOptionText !== null ? (
      <ViewHeading
        {...rest}
        isDropdown={shouldUseDropdown}
        secondaryText={
          value !== Workspace.none
            ? value.name
            : workspaces.length === 1
            ? alwaysUseDropdown
              ? allOptionText
              : workspaces[0].name
            : allOptionText
        }
        viewHeadingSize={viewHeadingSize}
      />
    ) : (
      <ViewHeading
        {...rest}
        isDropdown={shouldUseDropdown}
        secondaryText={
          value !== Workspace.none
            ? value.name
            : workspaces.length > 0
            ? value.name
            : 'No workspaces available'
        }
        viewHeadingSize={viewHeadingSize}
      />
    )
  return shouldUseDropdown ? (
    <Dropdown
      button={heading}
      autoDismiss={!searcher}
      isOpen={searcher?.editing}
      position="bottom-start"
      dataTest="workspace-selector-dropdown"
      onButtonClick={searcher?.startEditing}
      onRequestClose={searcher?.stopEditing}
    >
      {!allOptionDropdownItem && allOptionText && (
        <>
          <DropdownItem
            active={value === Workspace.none}
            checkmark={value === Workspace.none}
            data-test="all-workspaces"
            onClick={() => onChange(Workspace.none)}
          >
            {allOptionText}
          </DropdownItem>
          <DropdownDivider />
        </>
      )}

      {showClearButton && searcher?.searchText !== '' ? (
        <div className={styles.actionHeadingContainer}>
          <DropdownTitle>{dropdownTitle}</DropdownTitle>
          <button
            className={styles.clearTextButton}
            onClick={() => searcher?.setSearchText('')}
          >
            Clear
          </button>
        </div>
      ) : (
        <DropdownTitle>{dropdownTitle}</DropdownTitle>
      )}

      {searcher && <WorkspaceSearch searcher={searcher} />}
      <DropdownScroll>
        {allOptionText && allOptionDropdownItem && (
          <DropdownItem
            active={value === Workspace.none}
            checkmark={value === Workspace.none}
            data-test="all-workspaces"
            onClick={() => {
              searcher?.stopEditing()
              onChange(Workspace.none)
            }}
          >
            {allOptionDropdownItem}
          </DropdownItem>
        )}
        {workspaces.map((workspace) => (
          <DropdownItem
            key={Id.assert(workspace.id)}
            active={workspace === value}
            checkmark={workspace === value}
            onClick={() => {
              searcher?.stopEditing()
              onChange(workspace)
            }}
            data-test={`workspace-${workspace.id}-dropdown-item`}
          >
            {renderName ? renderName(workspace) : workspace.name}
          </DropdownItem>
        ))}
      </DropdownScroll>
    </Dropdown>
  ) : (
    heading
  )
})

interface WorkspaceSearchProps {
  searcher: IWorkspaceSearcher
}

const WorkspaceSearch = observer(({ searcher }: WorkspaceSearchProps) => {
  const change = React.useCallback(
    (e: any) => {
      searcher.setSearchText(e.target.value)
    },
    [searcher.setSearchText]
  )

  return (
    <div className={styles.search}>
      <input
        type="text"
        value={searcher.searchText}
        placeholder="Search"
        className={styles.searchInput}
        onChange={change}
        onClick={(evt) => evt.stopPropagation()}
        data-test="workspace-search-input"
      />
      <Icon name="search" />
    </div>
  )
})
